import { supabase } from "@/integrations/supabase/client";

type RoleHint = "employer" | "job_seeker";

export async function ensureProfile(userId: string, roleHint?: RoleHint) {
  console.log('🔧 [ensureProfile] Starting with userId:', userId, 'roleHint:', roleHint);
  
  // Try to read profile first
  const { data: existing, error: readErr } = await supabase
    .from("profiles")
    .select("user_id, role, onboarding_completed, verification_status")
    .eq("user_id", userId)
    .maybeSingle();

  console.log('🔧 [ensureProfile] Existing profile check:', { existing, readErr });

  if (!readErr && existing) {
    console.log('🔧 [ensureProfile] Found existing profile:', existing.role);
    return existing;
  }

  // If no profile yet and we know why the user is here, create one
  if (roleHint) {
    console.log('🔧 [ensureProfile] Creating new profile with role:', roleHint);
    
    // Get referral code from session storage if available
    const referralCode = sessionStorage.getItem("referral_code");
    console.log('🔧 [ensureProfile] Referral code from session:', referralCode);
    
    const profileData: any = {
      user_id: userId,
      role: roleHint,
      onboarding_completed: false,
      verification_status: "pending",
    };
    
    // Add referral code to profile if available
    if (referralCode) {
      profileData.referral_code = referralCode;
    }
    
    const { data: created, error: insErr } = await supabase
      .from("profiles")
      .insert(profileData)
      .select("user_id, role, onboarding_completed, verification_status")
      .single();

    console.log('🔧 [ensureProfile] Profile creation result:', { created, insErr });

    // If profile created successfully and we have a referral code, create referral record
    if (!insErr && created && referralCode) {
      console.log('🔧 [ensureProfile] Creating referral record for code:', referralCode);
      
      // Get affiliate ID for this referral code
      const { data: affiliateData, error: affiliateError } = await supabase
        .from('affiliates')
        .select('id')
        .eq('code', referralCode)
        .eq('status', 'approved')
        .single();
      
      if (!affiliateError && affiliateData) {
        // Get user email from auth
        const { data: { user } } = await supabase.auth.getUser();
        const userEmail = user?.email || '';
        
        // Create referral record
        const { error: referralError } = await supabase
          .from('referrals')
          .insert({
            affiliate_id: affiliateData.id,
            referred_user_id: userId,
            email: userEmail,
            status: 'pending',
            bounty_cents: 0
          });
        
        if (referralError) {
          console.error('❌ [ensureProfile] Failed to create referral record:', referralError);
        } else {
          console.log('✅ [ensureProfile] Referral record created successfully');
        }
      }
    }

    // In case of race/unique violation, read again
    if (insErr && (insErr as any).code === "23505") {
      console.log('🔧 [ensureProfile] Race condition detected, reading again...');
      const { data: again } = await supabase
        .from("profiles")
        .select("user_id, role, onboarding_completed, verification_status")
        .eq("user_id", userId)
        .maybeSingle();
      console.log('🔧 [ensureProfile] Race condition resolution:', again);
      return again ?? null;
    }

    return created ?? null;
  }

  console.log('🔧 [ensureProfile] No role hint provided, returning null');
  return null;
}
